import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'rxjs/add/operator/finally';
import debounce from 'lodash/debounce';
import { Subscription } from 'rxjs/Subscription';
import { useJiraServiceManagementProjectNavigation } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/common/project-sidebar-navigation-resource/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import useServiceProjectFeatures from '@atlassian/jira-servicedesk-features-store';
import { usePathParam } from '@atlassian/react-resource-router';
import updateFeatureService from '../../../services/common/update-feature/index.tsx';
import type { SavingState, UpdateProps } from './types';
import { fireSuccessAnalytics, fireFailureAnalytics } from './utils';

const useServiceProjectFeaturesController = () => {
	const {
		data,
		update: updateFeatureResource,
		refresh: refreshFeatureResource,
	} = useServiceProjectFeatures();
	const { refresh: refreshServiceManagementNavigation } =
		useJiraServiceManagementProjectNavigation();

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const [saving, setSavingState] = useState<SavingState>({} as SavingState);
	const [projectKey = ''] = usePathParam('projectKey');
	const subscriptionsRef = useRef<Subscription>(useMemo(() => new Subscription(), []));
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const refreshResources = useMemo(
		() =>
			debounce(
				() => {
					refreshFeatureResource();
					refreshServiceManagementNavigation();
				},
				1000,
				{ trailing: true },
			),
		[refreshFeatureResource, refreshServiceManagementNavigation],
	);

	const update = useCallback(
		({ feature, enabled, onSuccess, onFail }: UpdateProps) => {
			if (projectKey) {
				setSavingState((prevState) => ({
					...prevState,
					[feature]: true,
				}));

				subscriptionsRef.current.add(
					updateFeatureService(projectKey, feature, enabled)
						.finally(() => {
							setSavingState((prevState) => ({
								...prevState,
								[feature]: false,
							}));
						})
						.subscribe(
							({ status, editable }) => {
								refreshResources();
								updateFeatureResource((prevData) => ({
									...prevData,
									[feature]: { status, editable },
								}));

								fireSuccessAnalytics(createAnalyticsEvent({}), feature, enabled);
								onSuccess?.();
							},
							(error) => {
								fireFailureAnalytics(createAnalyticsEvent({}), error, feature, enabled);
								onFail?.(error);
							},
						),
				);
			}
		},
		[createAnalyticsEvent, projectKey, refreshResources, updateFeatureResource],
	);

	useEffect(
		() => () => {
			subscriptionsRef.current.unsubscribe();
		},
		[],
	);

	return { data, saving, update };
};

export default useServiceProjectFeaturesController;
